import React, { useMemo } from 'react';
import { Users2 } from 'lucide-react';
import type { OrgMember } from '../../../hooks/useOrgMembers';
import {
  LST_CARGO_LABELS,
  mapRoleTypeToCargo,
  type LstExecucao,
  type LstTemplate,
} from '../types';
import { LstMatrixMensal } from './LstMatrixMensal';
import type { HourCellData } from '../services/lstMatrixHourly';

interface CadeiaMatrizMensalProps {
  liderados: OrgMember[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  monthDates: string[];
  monthLabel: string;
  loadingExec: boolean;
  onCellClick: (template: LstTemplate, date: string, execucao: LstExecucao | null, code: string) => void;
  onHourBlockClick?: (cell: HourCellData) => void;
}

/**
 * Visão de Matriz Mensal por liderado — pauta da reunião de camadas.
 * Permite ao gestor abrir o Trabalho Padronizado completo do mês de cada
 * liderado direto e auditar célula a célula.
 */
export const CadeiaMatrizMensal: React.FC<CadeiaMatrizMensalProps> = ({
  liderados,
  selectedId,
  onSelect,
  templates,
  execucoes,
  monthDates,
  monthLabel,
  loadingExec,
  onCellClick,
  onHourBlockClick,
}) => {
  const selected = useMemo(
    () => liderados.find((l) => l.id === selectedId) ?? null,
    [liderados, selectedId],
  );
  const cargo = mapRoleTypeToCargo(selected?.roleType ?? null);

  if (liderados.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <Users2 size={28} className="text-slate-300 dark:text-slate-600" />
        <p className="text-[12px] font-medium text-slate-500 dark:text-slate-400">
          Nenhum liderado direto vinculado.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Seletor de liderado */}
      <div className="flex shrink-0 items-center gap-2 px-1 pb-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Liderado</span>
        <select
          value={selectedId ?? ''}
          onChange={(e) => onSelect(e.target.value)}
          className="h-8 min-w-[200px] rounded-md border border-slate-200 bg-white px-2 text-[12px] font-medium text-slate-700 outline-none transition-colors hover:border-slate-400 focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
        >
          {liderados.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} — {LST_CARGO_LABELS[mapRoleTypeToCargo(l.roleType)]}
            </option>
          ))}
        </select>
        {loadingExec && <span className="text-[11px] text-slate-400">Carregando execuções…</span>}
      </div>

      <div className="min-h-0 flex-1">
        {selected ? (
          <LstMatrixMensal
            key={selected.id}
            cargo={cargo}
            templates={templates}
            execucoes={execucoes}
            monthDates={monthDates}
            userId={selected.id}
            title={selected.name}
            subtitle={`${monthLabel} · ${LST_CARGO_LABELS[cargo]}`}
            onCellClick={onCellClick}
            onHourBlockClick={onHourBlockClick}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-[12px] text-slate-400">
            Selecione um liderado para ver a matriz.
          </div>
        )}
      </div>
    </div>
  );
};

export default CadeiaMatrizMensal;
